import React from "react";

import { ScrollArea } from "~/components/ui/scroll-area";
import { Skeleton } from "~/components/ui/skeleton";

/**
 * Loading skeleton displayed while creating a dispatch guide
 */
export const DispatchGuideLoadingSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="relative mb-2 border-b pb-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-start justify-between">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-6 w-48" />
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 overflow-y-auto pr-4">
        <div className="grid gap-6 py-4">
          {/* Order Summary Card Skeleton */}
          <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-5 shadow-sm">
            <Skeleton className="mb-3 h-6 w-48" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-1">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-24" />
                  {index === 0 && <Skeleton className="h-6 w-32" />}
                </div>
              ))}
              <div className="space-y-1 md:col-span-3">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          </div>

          {/* Items Section Skeleton */}
          <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-100 px-5 py-3">
              <Skeleton className="h-6 w-24" />
            </div>
            <div className="p-5">
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b border-gray-100 pb-4"
                  >
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <div className="flex gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-4">
              <div className="flex flex-col space-y-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="mt-1 flex items-center justify-between border-t border-gray-200 pt-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-32" />
                </div>
              </div>
            </div>
          </div>

          {/* Sales Condition Skeleton */}
          <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 shadow-sm">
            <Skeleton className="mb-3 h-6 w-40" />
            <div className="rounded-lg bg-white/60 p-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-1 h-3 w-32" />
            </div>
          </div>

          {/* Payment Terms Skeleton */}
          <div className="rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-5 shadow-sm">
            <Skeleton className="mb-3 h-6 w-40" />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-16" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-28" />
              </div>
            </div>
          </div>

          {/* Dispatch Guides Skeleton */}
          <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
              <Skeleton className="h-6 w-32" />
            </div>
            <div className="p-4">
              <div className="py-8 text-center">
                <Skeleton className="mx-auto mb-4 h-12 w-12 rounded-full" />
                <Skeleton className="mx-auto h-4 w-64" />
                <Skeleton className="mx-auto mt-2 h-3 w-48" />
              </div>
            </div>
          </div>

          {/* Loading Message */}
          <div className="rounded-xl border border-green-200 bg-green-50 p-5 text-center shadow-sm">
            <div className="flex items-center justify-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
              <p className="text-sm font-medium text-green-800">
                Creando guía de despacho...
              </p>
            </div>
            <p className="mt-1 text-xs text-green-600">
              Por favor espera mientras se genera la guía de despacho
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
